import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Table, Pagination, TableActions, TableAction, TableData, TableDataTypes } from '../shared/types';
import { LoaderService } from '../loader.service';
import { AuthService } from '../auth.service';
import { SlabApprovalRequestsService } from './slab-approval-requests.service';
import { ToasterService } from '../toaster.service';
import { Toaster, ToasterType } from '../shared/types/toaster.types';
import { SharedService } from '../shared/shared.service';
import Utils from 'src/app/utils/utils';
import { AUTHZ_FEATURES } from '../constants/permissions';
import { permissionSlugs } from '../auth.service';
import { AuthzAccessHintService } from '../shared/authz-access-hint/authz-access-hint.service';

@Component({
  selector: 'slab-approval-requests',
  templateUrl: './slab-approval-requests.component.html',
  providers: [SlabApprovalRequestsService]
})
export class SlabApprovalRequestsComponent implements OnInit {
  public authzAccessHint: { message: string; requestUrl: string } | null = null;
  public selectedStatus: string = 'pending';

  public listFilters: {
    requestStatus?: string;
  } = {
    requestStatus: 'PENDING'
  };

  private sort: { sortBy?: string; sortOrder?: number } = {};

  public table: Table = new Table({
    headers: {
      active: 'Status',
      cities: 'City',
      shift: 'Shift',
      serviceNames: 'Service',
      createdBy: 'Created By',
      startDate: 'Start Date',
      createdDate: 'Created Date',
      isFallbackSlab: 'Fallback Slab',
      poolConfig: 'Pool Configuration',
      action: 'Actions'
    },
    sortableColumnKeys: ['startDate'],
    pagination: new Pagination(0, 15, 150, [15, 20]),
    data: []
  });

  private cities = [];
  private serviceIdToName: { [id: string]: string } = {};

  constructor(
    private approvalRequestsService: SlabApprovalRequestsService,
    private loaderService: LoaderService,
    private router: Router,
    private accessRights: AuthService,
    public toastService: ToasterService,
    private sharedService: SharedService,
    private authzAccessHintService: AuthzAccessHintService,
  ) { }

  async ngOnInit() {
    await this.fetchAllCities();
    await this.fetchTableData();
    await this.loadAccessHint();
  }

  private async loadAccessHint(): Promise<void> {
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('slab');
  }



  async fetchAllCities() {
    const citiesRes: any = await this.withLoader(() => this.sharedService.fetchCities().toPromise());
    this.cities = citiesRes.data.cities;
  }


  async fetchTableData() {
    await this.withLoader(async () => {
      const permissions: any = await this.accessRights.getPermissions();
      const permissionSlugs = this.accessRights.normalizePermissionSlugs(permissions);
      const response: any = await this.approvalRequestsService.fetchApprovalRequests({
        pageNumber: this.table.pagination.pageNo,
        pageLimit: this.table.pagination.perPage,
        requestStatus: this.listFilters.requestStatus,
        sortBy: this.sort.sortBy,
        sortOrder: this.sort.sortOrder,
      }).toPromise();

      this.table.pagination.count = response.documentCount || 0;
      const rows = response.data || [];
      await this.enrichServiceNameCacheForSlabs(rows.map((row: any) => row.newValue || {}));
      this.table.data = rows.map((row: any) => {
        const slab = row.newValue || {};
        const cities = slab.cities || [];
        const shift = Array.isArray(slab.shift) ? slab.shift : (slab.shift != null ? [slab.shift] : []);
        return {
          _id: row._id,
          requestStatus: row.requestStatus,
          createdBy: row.createdBy,
          resourceId: row.resourceId,
          resource: row.resource,
          key: row.key,
          version: row.version,
          newValue: slab,
          oldValue: row.oldValue || null,
          active: this.formatRequestStatus(row.requestStatus),
          cities: this.formatCityNames(cities),
          shift: shift.join(', '),
          serviceNames: this.formatServiceNames(slab),
          startDate: slab.startDate,
          createdDate: this.formatCreatedDate(row.createdOn),
          isFallbackSlab: slab.isFallbackSlab ? 'Yes' : 'No',
          poolConfig: Utils.formatPoolConfigFromSlab(slab),
          action: this.createViewAction(row),
        };
      });
    });
  }

  formatCityNames(cityRefs: string[]) {
    return (cityRefs || [])
      .map((ref) => {
        const city = this.cities.find((c: any) =>
          String(c._id) === String(ref) || c.displayName === ref
        );
        return city ? city.displayName : ref;
      })
      .join(', ');
  }

  private async enrichServiceNameCacheForSlabs(slabs: any[]): Promise<void> {
    const cityObjs: any[] = [];
    for (const slab of slabs) {
      const refs = slab.cities || [];
      for (const ref of refs) {
        const city = this.cities.find((c: any) =>
          String(c._id) === String(ref) || c.displayName === ref
        );
        if (city && !cityObjs.some((c) => String(c._id) === String(city._id))) {
          cityObjs.push(city);
        }
      }
    }
    await Promise.all(cityObjs.map(async (city) => {
      try {
        const res: any = await this.sharedService.fetchServices(city._id).toPromise();
        const raw = res && res.data;
        const serviceDetails = Array.isArray(raw) ? raw : Object.values(raw || {});
        for (const serviceDetail of serviceDetails) {
          const name = serviceDetail.service && serviceDetail.service.name;
          if (name && serviceDetail._id) {
            this.serviceIdToName[String(serviceDetail._id)] = name;
          }
        }
      } catch (error) {
        // ignore per-city failures; fall back to raw ids in the table
      }
    }));
  }

  formatServiceNames(slab: any): string {
    const names = slab && slab.serviceNames;
    if (Array.isArray(names) && names.length > 0) {
      return names.join(', ');
    }
    const ids = (slab && (slab.serviceTypes || slab.serviceDetailIds)) || [];
    if (!Array.isArray(ids) || !ids.length) {
      return '';
    }
    return ids.map((id: string) => this.serviceIdToName[String(id)] || id).join(', ');
  }

  formatCreatedDate(createdOn: any) {
    if (!createdOn) {
      return '';
    }
    const num = Number(createdOn);
    if (!isNaN(num) && num > 1000000000) {
      const date = new Date(num);
      return date.toISOString().split('T')[0];
    }
    const raw = typeof createdOn === 'string' ? createdOn : String(createdOn);
    return raw.split('T')[0];
  }

  formatRequestStatus(status: string) {
    if (!status) {
      return '';
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  createViewAction(row: any) {
    const actionArr = [];
    const canView = this.accessRights.isAuthorizedFor(
      permissionSlugs(AUTHZ_FEATURES.SLAB, ['view', 'create', 'approve'], this.accessRights.getAuthzPanels()),
      AUTHZ_FEATURES.SLAB);
    if (canView) {
      actionArr.push(
        new TableAction({
          onClick: () => this.openPreview(row),
          text: 'View'
        })
      );
    }
    return new TableData({
      actions: new TableActions(actionArr),
      type: TableDataTypes.ACTION
    });
  }

  openPreview(row: any) {
    const requestId = row && row._id;
    if (!requestId) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: 'Approval request id is missing.'
      }));
      return;
    }
    this.approvalRequestsService.storePreviewForRequest(requestId, {
      requestId,
      requestStatus: row.requestStatus,
      operation: row.operation,
      createdBy: row.createdBy,
      statusChangedBy: row.statusChangedBy,
      resourceId: row.resourceId,
      resource: row.resource,
      key: row.key,
      version: row.version,
      newValue: row.newValue || {},
      oldValue: row.oldValue || null,
    });
    this.router.navigate(['/slab-approval-requests', requestId], { queryParams: { preview: 'approval' } });
  }

  async sortByHeader(header: string) {
    const sortOrder = header.includes('-') ? -1 : 1;
    const column = header.replace('-', '');
    if (column !== 'startDate') {
      return;
    }
    this.sort = { sortBy: column, sortOrder };
    this.table.pagination.setPageNoToZero();
    await this.fetchTableData();
  }

  onStatusChange() {
    this.table.pagination.setPageNoToZero();
    const statusMap: { [key: string]: string | undefined } = {
      all: undefined,
      pending: 'PENDING',
      approved: 'APPROVED',
      rejected: 'REJECTED',
    };
    this.listFilters.requestStatus = statusMap[this.selectedStatus];
    this.fetchTableData();
  }



  async withLoader<T>(cb: () => Promise<T>): Promise<T> {
    try {
      this.loaderService.openLoading();
      return await cb();
    } finally {
      this.loaderService.closeLoading();
    }
  }
}
